import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RefreshCw, Link2, Unlock, Copy, Eye, ShieldAlert, ArrowLeft, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({});

const StatusBadge = ({ value }) => {
  const map = {
    active: "bg-green-100 text-green-700",
    past_due: "bg-amber-100 text-amber-700",
    canceled: "bg-red-100 text-red-700",
    paid: "bg-green-100 text-green-700",
    unclaimed: "bg-slate-100 text-slate-600",
    pending_claim: "bg-amber-100 text-amber-700",
    claimed: "bg-blue-100 text-blue-700",
  };
  if (!value) return <span className="text-slate-300 text-xs">—</span>;
  return <Badge className={`${map[value] || "bg-slate-100 text-slate-600"} font-medium`}>{value}</Badge>;
};

const Copyable = ({ value, label }) => {
  if (!value) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(value); toast.success(`Copied ${label || "value"}`); }}
      className="inline-flex items-center gap-1 text-xs font-mono text-slate-600 hover:text-royal-blue max-w-[160px] truncate"
      title={value}
      data-testid={`copy-${label}`}
    >
      <span className="truncate">{value}</span>
      <Copy className="w-3 h-3 flex-shrink-0" />
    </button>
  );
};

const SUB_FILTERS = [
  { key: "unclaimed", label: "Unclaimed paid", params: { unlinked: true } },
  { key: "active", label: "Active", params: { subscription_status: "active" } },
  { key: "past_due", label: "Past due", params: { subscription_status: "past_due" } },
  { key: "canceled", label: "Canceled", params: { subscription_status: "canceled" } },
  { key: "missing_email", label: "Email missing", params: { missing_email: true } },
  { key: "pending", label: "Pending claims", params: { claim_status: "pending_claim" } },
];

const ORDER_FILTERS = [
  { key: "unlinked", label: "Unlinked (user_id null)", params: { unlinked: true } },
  { key: "paid", label: "Paid", params: { payment_status: "paid" } },
  { key: "missing_email", label: "Email missing", params: { missing_email: true } },
];

export default function AdminPayments() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [tab, setTab] = useState("subscriptions");
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resyncId, setResyncId] = useState("");
  const [details, setDetails] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null); // {type, id, email}
  const [linkEmail, setLinkEmail] = useState("");
  const [linkConfirm, setLinkConfirm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/nationals/me`, authHeader())
      .then((res) => {
        if ((res.data.roles || []).includes("admin")) setAuthorized(true);
        else { toast.error("Admin access required"); navigate("/portal"); }
      })
      .catch(() => navigate("/nationals/login"));
  }, [navigate]);

  const load = useCallback(async () => {
    if (!authorized) return;
    setLoading(true);
    try {
      const filter = SUB_FILTERS.concat(ORDER_FILTERS).find((f) => f.key === activeFilter);
      const params = { ...(filter ? filter.params : {}) };
      if (q.trim()) params.q = q.trim();
      const endpoint = tab === "subscriptions" ? "subscriptions" : "orders";
      const res = await axios.get(`${API}/admin/payments/${endpoint}`, { ...authHeader(), params });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [authorized, tab, q, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const doResync = async (id) => {
    const value = id || resyncId;
    if (!value) { toast.error("Enter a Stripe subscription or customer ID"); return; }
    const body = value.startsWith("cus_") ? { customer_id: value } : { subscription_id: value };
    try {
      const res = await axios.post(`${API}/admin/payments/resync`, body, authHeader());
      toast.success(`Resynced — email: ${res.data.email || "n/a"}, status: ${res.data.stripe_status || "n/a"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync failed");
    }
  };

  const doUnlock = async (orderId) => {
    try {
      await axios.post(`${API}/admin/payments/unlock-order`, { order_id: orderId }, authHeader());
      toast.success("Order unlocked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  };

  const submitLink = async () => {
    if (!linkEmail.trim()) { toast.error("Enter the target user's account email"); return; }
    if (!linkConfirm) { toast.error("Please confirm this manual link"); return; }
    const body = {
      user_email: linkEmail.trim(),
      confirm: true,
      ...(linkTarget.type === "subscription" ? { subscription_id: linkTarget.id } : { order_id: linkTarget.id }),
    };
    try {
      const res = await axios.post(`${API}/admin/payments/link`, body, authHeader());
      toast.success(`Linked → access: ${res.data.grant?.access_level || "n/a"}, status: ${res.data.grant?.subscription_status || "n/a"}`);
      setLinkTarget(null); setLinkEmail(""); setLinkConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    }
  };

  if (!authorized) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Verifying admin access…</div>;
  }

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-payments-page">
      <section className="bg-royal-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate("/portal")} className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-2" data-testid="back-to-portal">
              <ArrowLeft className="w-4 h-4" /> Portal
            </button>
            <h1 className="font-heading text-3xl flex items-center gap-2">
              <ShieldAlert className="w-7 h-7 text-kingdom-gold" /> Payments &amp; Subscriber Claims
            </h1>
            <p className="text-slate-300 text-sm mt-1">Troubleshoot paid subscribers, pending claims and Stripe-linked purchases.</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Resync toolbar */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-center gap-2">
          <RefreshCw className="w-4 h-4 text-royal-blue" />
          <span className="text-sm font-medium text-royal-blue">Resync from Stripe:</span>
          <Input
            value={resyncId}
            onChange={(e) => setResyncId(e.target.value)}
            placeholder="sub_… or cus_…"
            className="max-w-xs"
            style={{ fontSize: "16px" }}
            data-testid="resync-input"
          />
          <Button size="sm" onClick={() => doResync()} data-testid="resync-btn">Resync</Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {["subscriptions", "orders"].map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setActiveFilter(null); }}
              className={`px-4 py-2 rounded font-semibold text-sm capitalize ${tab === t ? "bg-royal-blue text-white" : "bg-white text-slate-600 border border-slate-200"}`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search + filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search email, customer ID, subscription/session/order ID…"
                className="pl-10"
                style={{ fontSize: "16px" }}
                data-testid="payments-search-input"
              />
            </div>
            <Button size="sm" onClick={load} data-testid="payments-search-btn">Search</Button>
            {(q || activeFilter) && (
              <Button size="sm" variant="ghost" onClick={() => { setQ(""); setActiveFilter(null); }} data-testid="clear-filters-btn">Clear</Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(tab === "subscriptions" ? SUB_FILTERS : ORDER_FILTERS).map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(activeFilter === f.key ? null : f.key)}
                className={`text-xs px-3 py-1 rounded-full border ${activeFilter === f.key ? "bg-kingdom-gold text-white border-kingdom-gold" : "bg-white text-slate-600 border-slate-300"}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-400" data-testid="no-results">No records found.</div>
          ) : tab === "subscriptions" ? (
            <table className="w-full text-sm" data-testid="subscriptions-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Buyer Email</th>
                  <th className="text-left px-3 py-2">Linked User</th>
                  <th className="text-left px-3 py-2">Sub Status</th>
                  <th className="text-left px-3 py-2">Claim</th>
                  <th className="text-left px-3 py-2">Plan / Access</th>
                  <th className="text-left px-3 py-2">Customer ID</th>
                  <th className="text-left px-3 py-2">Subscription ID</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.provider_subscription_id || `sub-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sub-row-${r.provider_subscription_id}`}>
                    <td className="px-3 py-2">{r.email || <span className="text-red-400 text-xs">missing</span>}</td>
                    <td className="px-3 py-2 text-xs">{r.linked_user_email || (r.user_id ? r.user_id : <span className="text-slate-300">—</span>)}</td>
                    <td className="px-3 py-2"><StatusBadge value={r.subscription_status} /></td>
                    <td className="px-3 py-2"><StatusBadge value={r.claim_status} /></td>
                    <td className="px-3 py-2 text-xs">{r.internal_plan_id || "—"}<br /><span className="text-slate-400">{r.access_level || ""}</span></td>
                    <td className="px-3 py-2"><Copyable value={r.provider_customer_id} label="customer-id" /></td>
                    <td className="px-3 py-2"><Copyable value={r.provider_subscription_id} label="subscription-id" /></td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDetails(r)} title="Details" data-testid={`details-${r.provider_subscription_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => doResync(r.provider_subscription_id)} title="Resync" data-testid={`row-resync-${r.provider_subscription_id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-royal-purple" onClick={() => { setLinkTarget({ type: "subscription", id: r.provider_subscription_id }); setLinkEmail(r.email || ""); setLinkConfirm(false); }} title="Link to user" data-testid={`row-link-${r.provider_subscription_id}`}><Link2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Buyer Email</th>
                  <th className="text-left px-3 py-2">Linked User</th>
                  <th className="text-left px-3 py-2">Payment</th>
                  <th className="text-left px-3 py-2">Fulfillment</th>
                  <th className="text-left px-3 py-2">Session ID</th>
                  <th className="text-left px-3 py-2">Customer ID</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.order_id || `ord-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`order-row-${r.order_id}`}>
                    <td className="px-3 py-2">{r.buyer_email || <span className="text-red-400 text-xs">missing</span>}</td>
                    <td className="px-3 py-2 text-xs">{r.linked_user_email || (r.user_id ? r.user_id : <span className="text-slate-300">—</span>)}</td>
                    <td className="px-3 py-2"><StatusBadge value={r.payment_status} /></td>
                    <td className="px-3 py-2 text-xs">{r.fulfillment_status || "—"}</td>
                    <td className="px-3 py-2"><Copyable value={r.provider_session_id} label="session-id" /></td>
                    <td className="px-3 py-2"><Copyable value={r.provider_customer_id} label="customer-id" /></td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDetails(r)} title="Details" data-testid={`order-details-${r.order_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={() => doUnlock(r.order_id)} title="Unlock" data-testid={`order-unlock-${r.order_id}`}><Unlock className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-royal-purple" onClick={() => { setLinkTarget({ type: "order", id: r.order_id }); setLinkEmail(r.buyer_email || ""); setLinkConfirm(false); }} title="Link to user" data-testid={`order-link-${r.order_id}`}><Link2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Details dialog */}
      <Dialog open={!!details} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent className="max-w-lg" data-testid="details-dialog">
          <DialogHeader><DialogTitle>Record details</DialogTitle></DialogHeader>
          <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded overflow-auto max-h-[60vh]">
            {details ? JSON.stringify(details, null, 2) : ""}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Manual link dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(o) => !o && setLinkTarget(null)}>
        <DialogContent className="max-w-md" data-testid="link-dialog">
          <DialogHeader><DialogTitle>Link {linkTarget?.type} to a user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This manually grants the purchase/subscription to a user account. The action is audit-logged.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-500">Target user account email</label>
              <Input value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="user@example.com" style={{ fontSize: "16px" }} data-testid="link-email-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={linkConfirm} onChange={(e) => setLinkConfirm(e.target.checked)} data-testid="link-confirm-checkbox" />
              I confirm this manual link after verifying ownership.
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button className="btn-gold" onClick={submitLink} data-testid="link-submit-btn">Confirm Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
